import { useLocale } from 'next-intl';
import { useInfiniteQuery } from 'react-query';

import { useBrand } from '@src/contexts/brand-context';
import { useProfile } from '@src/features/profile/contexts/profile-context';
import { MyTripsPage } from '@src/generated/dato-cms';
import { tripService } from '@src/services';
import { productApiNetworkService } from '@src/services/api/product-api-network.service';
import { PaginatedResult, PaginationProps } from '@src/services/api/trips/trip.repository';
import { TripListItem } from '@src/types/api/models';
import { getRoomPageUrl } from '@src/utils/urls';

type MyTripsPageApiResponse = {
  success: true;
  data: MyTripsPage;
};

const RESULTS_PER_PAGE = 5;
const initialPageParams: PaginationProps = {
  limit: RESULTS_PER_PAGE,
  offset: 0,
};
const getNextPageParam: <T>(paginatedResult: PaginatedResult<T>) => PaginationProps | undefined = ({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  data,
  ...paginationData
}) =>
  paginationData.hasMore
    ? {
        ...paginationData,
        offset: paginationData.offset + paginationData.limit,
      }
    : undefined;

const createMap = <T>(items: T[], predicate: (item: T) => string) => {
  const map = new Map<string, T>();
  for (const item of items) {
    const key = predicate(item);
    if (!map.has(key)) {
      map.set(key, item);
    }
  }
  return map;
};

export const usePastCanceledTripsQuery = () => {
  const locale = useLocale();
  const { data: profile } = useProfile();
  const { brand } = useBrand();
  return useInfiniteQuery<PaginatedResult<TripListItem>>(
    ['past-canceled-trips', locale, profile, productApiNetworkService.defaults.headers.Authorization],
    async ({ pageParam = initialPageParams }) => {
      const { data: trips, ...paginationData } = await tripService.getPastCanceledTrips(locale, pageParam);

      // No trips to enrich - return early with empty data
      if (trips.length === 0) {
        return {
          data: [],
          ...paginationData,
        };
      }

      const propertyIds = trips.map((trip) => trip.property.id);
      const unitGroupIds = trips.map((trip) => trip.unitGroup.id);

      const searchParams = new URLSearchParams({
        propertyIds: propertyIds.join(','),
        unitGroupIds: unitGroupIds.join(','),
        locale,
      });

      const myTripsPageResponse = await fetch(`/api/my-trips-page?${searchParams.toString()}`);

      if (!myTripsPageResponse.ok) {
        throw new Error(`Failed to fetch MyTripsPage data: ${myTripsPageResponse.status}`);
      }

      const myTripsPageResult = (await myTripsPageResponse.json()) as MyTripsPageApiResponse;

      if (!myTripsPageResult.success) {
        throw new Error('MyTripsPage API request failed');
      }

      const myTripsPage = myTripsPageResult.data;
      const { properties, unitGroups } = {
        properties: createMap(myTripsPage.properties, ({ id }) => id),
        unitGroups: createMap(myTripsPage.unitGroups, ({ id }) => id),
      };
      const tripsWithRoomPageUrls = trips.map(
        ({ property: { id: propertyId }, unitGroup: { id: unitGroupId }, ...trip }) => {
          const property = properties.get(propertyId)!;
          const unitGroup = unitGroups.get(unitGroupId)!;
          return {
            ...trip,
            property,
            unitGroup,
            roomPageUrl:
              getRoomPageUrl({
                websiteUrl: brand.websiteUrl,
                countrySlug: property?.country.slug,
                citySlug: property?.city.slug,
                districtSlug: property?.district.slug,
                propertySlug: property?.slug,
                unitGroupSlug: unitGroup?.slug,
              }) ?? '',
          };
        },
      );

      return {
        data: tripsWithRoomPageUrls,
        ...paginationData,
      };
    },
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      enabled: !!profile && !!productApiNetworkService.defaults.headers.Authorization,
      getNextPageParam,
    },
  );
};
